import { Injectable, inject , signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { UnitSettings } from '../../models/unit-settings';
import { MapSettings } from '../../models/map-settings';
import { RoutingSettings } from '../../models/routing-settings';
import { VoiceGuidenceSettings } from '../../models/voice-guidence-settings';
import { Address } from '../../models/address';
import { HttpService } from '../core/http.service';
import { WidgetService } from '../platform/widget.service';

export const enum Unit {
  Metric = "metric",
  Imperial = "imperial"
}

export const enum MapSkin {
  Light = "light",
  Dark = "dark"
}

export const enum AddressType {
  Home = "home",
  Work = "work"
}

export const Language = [
  { "code": "id", "name": "Bahasa Indonesia" },
  { "code": "ca", "name": "Català" },
  { "code": "cs", "name": "Čeština" },
  { "code": "cy", "name": "Cymraeg" },
  { "code": "da", "name": "Dansk" },
  { "code": "de", "name": "Deutsch" },
  { "code": "et", "name": "Eesti" },
  { "code": "en", "name": "English" },
  { "code": "es", "name": "Español" },
  { "code": "eu", "name": "Euskara" },
  { "code": "fr", "name": "Français" },
  { "code": "ga", "name": "Gaeilge" },
  { "code": "gl", "name": "Galego" },
  { "code": "hr", "name": "Hrvatski" },
  { "code": "is", "name": "Íslenska" },
  { "code": "it", "name": "Italiano" },
  { "code": "lv", "name": "Latviešu" },
  { "code": "lb", "name": "Lëtzebuergesch" },
  { "code": "lt", "name": "Lietuvių" },
  { "code": "hu", "name": "Magyar" },
  { "code": "mt", "name": "Malti" },
  { "code": "nl", "name": "Nederlands" },
  { "code": "no", "name": "Norsk" },
  { "code": "pl", "name": "Polski" },
  { "code": "br", "name": "Português (Brasil)" },
  { "code": "pt", "name": "Português (Portugal)" },
  { "code": "ro", "name": "Română" },
  { "code": "sq", "name": "Shqip" },
  { "code": "sk", "name": "Slovenčina" },
  { "code": "sl", "name": "Slovenščina" },
  { "code": "fi", "name": "Suomi" },
  { "code": "se", "name": "Svenska" },
  { "code": "vi", "name": "Tiếng Việt" },
  { "code": "tr", "name": "Türkçe" },
  { "code": "el", "name": "Ελληνικά" },
  { "code": "bg", "name": "Български" },
  { "code": "mk", "name": "Македонски" },
  { "code": "ru", "name": "Русский" },
  { "code": "sr", "name": "Српски" },
  { "code": "uk", "name": "Українська" },
  { "code": "hi", "name": "हिन्दी" },
  { "code": "ja", "name": "日本語" },
  { "code": "zh", "name": "中文（简体）" }
]

export const UNIT_METRIC: UnitSettings = { Distance: Unit.Metric, Temperature: Unit.Metric, Speed: Unit.Metric, Pressure: Unit.Metric };
export const UNIT_IMPERIAL: UnitSettings = { Distance: Unit.Imperial, Temperature: Unit.Imperial, Speed: Unit.Imperial, Pressure: Unit.Imperial };
export const MAP_DEFAULT: MapSettings = { Skin: MapSkin.Light, POIs: false };
export const ROUTING_DEFAULT: RoutingSettings = { TollRoads: true, Ferries: true, Highways: true };
export const VOICEGUIDENCE_DEFAULT: VoiceGuidenceSettings = { Mute: false };
export const VOICEINPUTTUTORIALDONE_DEFAULT: boolean = false;

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private isAuthorized: boolean = false;
  private readonly _unit = signal<UnitSettings>(undefined as unknown as UnitSettings);
  public ObservableUnit = new BehaviorSubject<UnitSettings | null>(null);
  private Routing!: RoutingSettings;
  public ObservableRoutingSettings = new BehaviorSubject<RoutingSettings | null>(null);
  private VoiceGuidence!: VoiceGuidenceSettings;
  private readonly _map = signal<MapSettings>(undefined as unknown as MapSettings);
  public ObservableMap = new BehaviorSubject<MapSettings | null>(null);
  private VoiceInputTutorialDone!: boolean;
  private Addresses: Address[] = [];
  private DisabledChargingStationIds: string[] = [];
  private FavoriteChargingStationIds: string[] = [];
  private DisabledChargingOperatorIds: number[] = [];
  private FavoriteChargingOperatorIds: number[] = [];
  private Tracking: boolean = true;
  public ObservableTracking = new BehaviorSubject<boolean>(true);
  public acceptCookies: boolean = true;

  private readonly httpService = inject(HttpService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    this.initSettings();
  }

  // load settings from localstorage
  // set default values if the localstorage values is empty
  public initSettings(): void {
    this.Addresses = [];

    let unitSettings, routingSettings, voiceguidenceSettings, mapSettings, voiceInputTutorialDoneSettings,
      disabledChargingStationsSettings, favoriteChargingStationsSettings, disabledChargingOperatorsSettings,
      favoriteChargingOperatorsSettings;
    try {
      unitSettings = JSON.parse(localStorage.getItem("unit")!);
    } catch (e) { }
    if (typeof (unitSettings) == 'object' && unitSettings != null) {
      this.setUnit(this.unitJSONtoUnitSettings(unitSettings));
    }
    else if (unitSettings === "metric") {
      this.setUnit(UNIT_METRIC);
    }
    else if (unitSettings === "imperial") {
      this.setUnit(UNIT_IMPERIAL);
    }
    else {
      this._unit.set(UNIT_METRIC);

      if (!this.widgetService.hasAnyUnit()) this.httpService.getCheck().subscribe((resp) => {
        if (resp.substring(0, 2) == "GB" || resp.substring(0, 2) == "US") {
          this.setUnit(UNIT_IMPERIAL);
        }
      });
    }

    try {
      routingSettings = JSON.parse(localStorage.getItem("routing")!);
    } catch (e) { }
    if (typeof (routingSettings) == 'object' && routingSettings != null) {
      this.setRounting(this.routingJSONtoRoutingSettings(routingSettings));
    } else {
      this.setRounting(ROUTING_DEFAULT);
    }

    try {
      voiceguidenceSettings = JSON.parse(localStorage.getItem("voiceguidence")!);
    } catch (e) { }
    if (typeof (voiceguidenceSettings) == 'object' && voiceguidenceSettings != null) {
      this.setVoiceGuidence(this.voiceGuidenceJSONtoVoiceGuidenceSettings(voiceguidenceSettings))
    } else {
      this.setVoiceGuidence(VOICEGUIDENCE_DEFAULT);
    }

    try {
      mapSettings = JSON.parse(localStorage.getItem("mapsettings")!);
    } catch (e) { }
    if (typeof (mapSettings) == 'object' && mapSettings != null) {
      this.setMap(this.mapJSONtoMapSettings(mapSettings));
    } else {
      this.setMap(MAP_DEFAULT);
    }

    try {
      voiceInputTutorialDoneSettings = JSON.parse(localStorage.getItem("voiceInputTutorialDone")!);
    } catch (e) { }
    if (voiceInputTutorialDoneSettings != null) {
      this.setVoiceInputTutorialDone(voiceInputTutorialDoneSettings);
    } else {
      this.setVoiceInputTutorialDone(VOICEINPUTTUTORIALDONE_DEFAULT);
    }

    try {
      disabledChargingStationsSettings = JSON.parse(localStorage.getItem("disabledChargingStationsEM")!);
    } catch (e) { }
    if (disabledChargingStationsSettings != null) {
      this.setDisabledChargingStationIds(disabledChargingStationsSettings);
    }

    try {
      favoriteChargingStationsSettings = JSON.parse(localStorage.getItem("favoriteChargingStationsEM")!);
    } catch (e) { }
    if (favoriteChargingStationsSettings != null) {
      this.setFavoriteChargingStationIds(favoriteChargingStationsSettings);
    }

    try {
      disabledChargingOperatorsSettings = JSON.parse(localStorage.getItem("disabledChargingOperatorsEM")!);
    } catch (e) { }
    if (disabledChargingOperatorsSettings != null) {
      this.setDisabledChargingOperatorIds(disabledChargingOperatorsSettings.map(Number));
    }

    try {
      favoriteChargingOperatorsSettings = JSON.parse(localStorage.getItem("favoriteChargingOperatorsEM")!);
    } catch (e) { }
    if (favoriteChargingOperatorsSettings != null) {
      this.setFavoriteChargingOperatorIds(favoriteChargingOperatorsSettings.map(Number));
    }

    this.applyWidgetOverrides();
  }

  // Widget mode: URL query parameters are applied on top of the stored settings so the
  // embedding page always gets what it asked for. Only the parameters that were given
  // are touched; everything else keeps the stored/default value.
  private applyWidgetOverrides(): void {
    if (!this.widgetService.isWidget()) return;
    const w = this.widgetService;

    if (w.hasAnyUnit()) {
      const current = this.getUnit();
      const pick = (name: 'distance' | 'temperature' | 'speed' | 'pressure', fallback: Unit): Unit => {
        const value = w.getUnit(name);
        return value == null ? fallback : (value === 'imperial' ? Unit.Imperial : Unit.Metric);
      };
      this.setUnit({
        Distance: pick('distance', current.Distance),
        Temperature: pick('temperature', current.Temperature),
        Speed: pick('speed', current.Speed),
        Pressure: pick('pressure', current.Pressure),
      });
    }

    const routing = this.getRouting();
    const tollRoads = w.getRouting('tollroads');
    const ferries = w.getRouting('ferries');
    const highways = w.getRouting('highways');
    if (tollRoads != null || ferries != null || highways != null) {
      this.setRounting({
        TollRoads: tollRoads ?? routing.TollRoads,
        Ferries: ferries ?? routing.Ferries,
        Highways: highways ?? routing.Highways,
      });
    }

    const mute = w.getMute();
    if (mute != null) {
      this.setVoiceGuidence({ Mute: mute });
    }

    // The widget renders in the light skin unless `skin=dark` asks otherwise — the stored
    // value is deliberately not consulted, so the embedding site gets the same look for every
    // visitor whatever an earlier visit stored. Not persisted either (see setMap).
    const map = this.getMap();
    this.setMap({
      Skin: w.getSkin() === 'dark' ? MapSkin.Dark : MapSkin.Light,
      POIs: w.getPois() ?? map.POIs,
    }, false);

    // `color_*` parameters: inline on the body, so they stay put when the skin changes.
    w.applyColors();

    // Hidden station / operator lists: the visitor could not see or edit what an earlier
    // session stored, so it must not steer routing or the map either. Cleared in memory only
    // (the private fields, not the persisting setters), so the stored lists come back when
    // the embedding page shows the lists again.
    if (!w.stationListsEnabled()) {
      this.FavoriteChargingStationIds = [];
      this.DisabledChargingStationIds = [];
    }
    if (!w.operatorListsEnabled()) {
      this.FavoriteChargingOperatorIds = [];
      this.DisabledChargingOperatorIds = [];
    }

    // `favoriteoperators` replaces the preferred list — also when the lists are hidden — and
    // takes those operators off the avoided list. Not persisted, like the skin.
    const favoriteOperators = w.getFavoriteOperatorIds();
    if (favoriteOperators != null) {
      this.FavoriteChargingOperatorIds = favoriteOperators;
      this.DisabledChargingOperatorIds = this.DisabledChargingOperatorIds.filter((id) => !favoriteOperators.includes(id));
    }
  }

  public setIsAuthorized(isAuthorized: boolean): void {
    this.isAuthorized = isAuthorized;
  }

  /** Reading this from a template registers the view as a consumer of the unit signal. */
  public getUnit(): UnitSettings {
    return this._unit();
  }

  public setUnit(unit: UnitSettings) {
    this._unit.set(unit);
    if (!this.isAuthorized) {
      localStorage.setItem("unit", JSON.stringify(unit).toLowerCase());
    }
    this.ObservableUnit.next(unit);
  }

  public unitJSONtoUnitSettings(unitJSON): UnitSettings {
    return {
      Distance: unitJSON.distance === "imperial" ? Unit.Imperial : Unit.Metric,
      Temperature: unitJSON.temperature === "imperial" ? Unit.Imperial : Unit.Metric,
      Speed: unitJSON.speed === "imperial" ? Unit.Imperial : Unit.Metric,
      Pressure: unitJSON.pressure === "imperial" ? Unit.Imperial : Unit.Metric,
    }
  }

  public getRouting(): RoutingSettings {
    return this.Routing;
  }

  public setRounting(routing: RoutingSettings) {
    this.Routing = routing;
    if (!this.isAuthorized) {
      localStorage.setItem("routing", JSON.stringify(routing).toLowerCase());
    }
    this.ObservableRoutingSettings.next(routing);
  }

  public routingJSONtoRoutingSettings(routingJSON): RoutingSettings {
    return {
      TollRoads: routingJSON.tollroads,
      Ferries: routingJSON.ferries,
      Highways: routingJSON.highways
    }
  }

  public getVoiceGuidence(): VoiceGuidenceSettings {
    return this.VoiceGuidence;
  }

  public setVoiceGuidence(voiceguidence: VoiceGuidenceSettings): void {
    this.VoiceGuidence = voiceguidence;
    if (!this.isAuthorized) {
      localStorage.setItem("voiceguidence", JSON.stringify(voiceguidence).toLowerCase());
    }
  }

  public voiceGuidenceJSONtoVoiceGuidenceSettings(voiceGuidenceJSON): VoiceGuidenceSettings {
    return {
      Mute: voiceGuidenceJSON.mute,
    }
  }

  /** Reading this from a template registers the view as a consumer of the map signal. */
  public getMap(): MapSettings {
    return this._map();
  }

  /**
   * `persist` is false for the widget's URL-driven skin: a theme the URL forces must not be
   * stored as the visitor's own preference.
   */
  public setMap(map: MapSettings, persist: boolean = true): void {
    this._map.set(map);
    if (persist && !this.isAuthorized) {
      localStorage.setItem("mapsettings", JSON.stringify(map).toLowerCase());
    }
    if (map.Skin === MapSkin.Dark) {
      document.body.classList.remove("lightSkin");
    }
    else {
      document.body.classList.add("lightSkin");
    }
    this.ObservableMap.next(map);
  }

  public mapJSONtoMapSettings(mapJSON): MapSettings {
    return {
      Skin: mapJSON.skin === "light" ? MapSkin.Light : MapSkin.Dark,
      POIs: mapJSON.pois
    }
  }

  public getVoiceInputTutorialDone(): boolean {
    return this.VoiceInputTutorialDone;
  }

  public setVoiceInputTutorialDone(voiceInputTutorialDone): void {
    this.VoiceInputTutorialDone = voiceInputTutorialDone;
    if (!this.isAuthorized) {
      localStorage.setItem("voiceInputTutorialDone", String(voiceInputTutorialDone));
    }
  }

  public getDisabledChargingOperatorIds(): number[] {
    return this.DisabledChargingOperatorIds;
  }

  public getContainsDisabledChargingOperatorId(chargingOperatorId: number): boolean {
    return this.DisabledChargingOperatorIds.includes(chargingOperatorId);
  }

  public setDisabledChargingOperatorIds(disabledChargingOperatorsList: number[]): void {
    this.DisabledChargingOperatorIds = disabledChargingOperatorsList;
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingOperatorsEM", JSON.stringify(disabledChargingOperatorsList));
    }
  }

  public getFavoriteChargingOperatorIds(): number[] {
    return this.FavoriteChargingOperatorIds;
  }

  public getContainsFavoriteChargingOperatorId(chargingOperatorId: number): boolean {
    return this.FavoriteChargingOperatorIds.includes(chargingOperatorId);
  }

  public setFavoriteChargingOperatorIds(favoriteChargingOperatorsList: number[]): void {
    this.FavoriteChargingOperatorIds = favoriteChargingOperatorsList;
    if (!this.isAuthorized) {
      localStorage.setItem("favoriteChargingOperatorsEM", JSON.stringify(favoriteChargingOperatorsList));
    }
  }

  public getDisabledChargingStationIds(): string[] {
    return this.DisabledChargingStationIds;
  }

  public getContainsDisabledChargingStationId(chargingStationId: string): boolean {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);

    return disabledIndex != -1;
  }

  public setDisabledChargingStationIds(disabledChargingStationList: string[]): void {
    this.DisabledChargingStationIds = disabledChargingStationList;
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingStationsEM", JSON.stringify(disabledChargingStationList));
    }
  }

  public getFavoriteChargingStationIds(): string[] {
    return this.FavoriteChargingStationIds;
  }

  public getContainsFavoriteChargingStationId(chargingStationId: string): boolean {
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);

    return favoriteIndex != -1;
  }

  public setFavoriteChargingStationIds(favoriteChargingStationList: string[]): void {
    this.FavoriteChargingStationIds = favoriteChargingStationList;
    if (!this.isAuthorized) {
      localStorage.setItem("favoriteChargingStationsEM", JSON.stringify(favoriteChargingStationList));
    }
  }

  private updateChargingStationIds(): void {
    if (!this.isAuthorized) {
      localStorage.setItem("disabledChargingStationsEM", JSON.stringify(this.DisabledChargingStationIds));
      localStorage.setItem("favoriteChargingStationsEM", JSON.stringify(this.FavoriteChargingStationIds));
    }
  }

  public setToNormalChargingStation(chargingStationId: string): void {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex != -1) {
      this.DisabledChargingStationIds.splice(disabledIndex, 1);
    }
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex != -1) {
      this.FavoriteChargingStationIds.splice(favoriteIndex, 1);
    }
    this.updateChargingStationIds();
  }

  public setToDisabledChargingStation(chargingStationId: string): void {
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex != -1) {
      this.FavoriteChargingStationIds.splice(favoriteIndex, 1);
    }
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex == -1) {
      this.DisabledChargingStationIds.push(chargingStationId);
    }
    this.updateChargingStationIds();
  }

  public setToFavoriteChargingStation(chargingStationId: string): void {
    const disabledIndex = this.DisabledChargingStationIds.indexOf(chargingStationId);
    if (disabledIndex != -1) {
      this.DisabledChargingStationIds.splice(disabledIndex, 1);
    }
    const favoriteIndex = this.FavoriteChargingStationIds.indexOf(chargingStationId);
    if (favoriteIndex == -1) {
      this.FavoriteChargingStationIds.push(chargingStationId);
    }
    this.updateChargingStationIds();
  }

  public addressesJSONtoAddresses(addresses): Address[] {
    const addressesEl: Address[] = addresses.map((address) => {
      let addressEl = new Address(
        address.AddressType === "home" ? AddressType.Home : AddressType.Work,
        address.Description,
        address.Location,
        address.BoundingBox);
      return addressEl;
    });
    return addressesEl;
  }

  public getAddresses(): Address[] {
    return this.Addresses;
  }

  public setAddresses(address: Address[]): void {
    this.Addresses = address;
  }

  public getHomeAddress(): Address | undefined {
    return this.Addresses.find((addressEl) => { return addressEl.AddressType == AddressType.Home });
  }

  public setHomeAddress(address: Address) {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Home;
    })) {
      this.Addresses.splice(index, 1);
    }

    this.Addresses.push(address);
  }

  public removeHomeAddress(): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Home;
    })) {
      this.Addresses.splice(index, 1);
    }
  }

  public getWorkAddress(): Address | undefined {
    return this.Addresses.find((addressEl) => { return addressEl.AddressType == AddressType.Work });
  }

  public setWorkAddress(address: Address): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Work;
    })) {
      this.Addresses.splice(index, 1);
    }

    this.Addresses.push(address);
  }

  public removeWorkAddress(): void {
    let index;
    if (this.Addresses.some((addressEl, i) => {
      index = i;
      return addressEl.AddressType === AddressType.Work;
    })) {
      this.Addresses.splice(index, 1);
    }
  }

  public getTracking(): boolean {
    return this.Tracking;
  }

  public setTracking(tracking: boolean) {
    this.Tracking = tracking;
    this.ObservableTracking.next(tracking);
  }
}
